import * as React from 'react';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import { useLocation } from 'react-router-dom';
import { NestedKeyOf, useFormRedux } from 'shared/utils/hooks/useFormRedux';
import { fetchDictionaries } from 'entities/cancer-protection/step-1/action/fetch-step-one';
import * as yup from 'yup';
import { updateField } from 'entities/cancer-protection/step-2';
import { validationSchema, validationSchemaInrured, validationSchemaInrureds } from 'entities/cancer-protection/step-2/utils/validationSchema';
import { InsuranceTermStep } from './ui/InsuranceTermStep';
import { IInsurer, IStepTwo } from 'entities/cancer-protection/step-2/types/StepTwo-inerface';
import { fetchCountries } from 'entities/cancer-protection/step-2/action/fetch-step-two';
import { Insurer } from './ui/Insurer';
import { Insured } from './ui/Insured';
import { CountriesData } from 'entities/cancer-protection/step-2/selsctor';
import { FieldValidationStep } from './ui/FieldValidationStep';
import { FormFieldsStepOne } from 'entities/cancer-protection/step-1/selector';

interface IPropsStepTwo {
    windowWidth: number;
    handleNext: () => void;
}

enum Steps {
    Insurer = "Insurer",
    InsuredPassportData = 'InsuredPassportData',
    Insured = "Insured",
    InsuranceTerm = "InsuranceTerm",
    FieldValidationStep = "FieldValidationStep",
}

export const StepTwo: React.FC<IPropsStepTwo> = ({ windowWidth, handleNext }) => {
    const dispatch = useAppDispatch();
    const [currentStep, setCurrentStep] = useState<Steps>(Steps.Insurer);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
    const { AgentSalesDataID } = useAppSelector(FormFieldsStepOne);
    const {
        formState,
        handleChange,
        handleBlur,
        handleDateChange,
        formatPhone,
        handlePhoneChange,
        handleAutocompleteChange,
        handleDepartmentCodeChange,
        handleDocumentNumberChange,
        handleDocumentSeriesChange,
        handleCheckboxChange,
        handleArrayDateChange,
        handleArrayPhoneChange
    } = useFormRedux(
        (state) => state.StepTwoCancer,
        updateField,
        validationSchema
    );

    const { DaDataAddres } = useAppSelector((state) => state.StepTwoCancer);

    useEffect(() => {
        dispatch(fetchCountries({ AgentSalesDataID: AgentSalesDataID }));
    }, []);

    const validateInsuranceTermStep = async () => {
        try {
            const schema = yup.object().shape({
                BeginDate: yup.string().required('Обязательное поле'),
            });

            await schema.validate(formState, { abortEarly: false });
            setErrors({});
            return true;
        } catch (err) {
            if (err instanceof yup.ValidationError) {
                const newErrors: Record<string, string> = {};
                err.inner.forEach((error) => {
                    if (error.path) {
                        newErrors[error.path] = error.message;
                    }
                });
                setErrors(newErrors);
                return false;
            }
            return false;
        }
    };

    const { Countries } = useAppSelector(CountriesData);
    const validatePolicyholderStep = async () => {
        try {
            const isRussia = Countries?.find(country => country.ID === formState.Insurer.CountryID)?.Name === 'РОССИЯ';
            const schema = validationSchemaInrured(isRussia, formState.PolicyholderIsSame);
            await schema.validate(formState, { abortEarly: false });
            setErrors({});
            return true;
        } catch (err) {
            if (err instanceof yup.ValidationError) {
                const newErrors: Record<string, string> = {};
                err.inner.forEach((error) => {
                    if (error.path) {
                        newErrors[error.path] = error.message;
                    }
                });
                setErrors(newErrors);
                return false;
            }
            return false;
        }
    };

    const validateInsuredStep = async () => {
        try {
            await validationSchemaInrureds.validate(formState, { abortEarly: false });
            setErrors({});
            return true;
        } catch (err) {
            if (err instanceof yup.ValidationError) {
                const newErrors: Record<string, string> = {};
                err.inner.forEach((error) => {
                    if (error.path) {
                        newErrors[error.path] = error.message;
                    }
                });
                setErrors(newErrors);
                return false;
            }
            return false;
        }
    };

    const handleNextStep = async () => {
        let isValid = false;
        switch (currentStep) {
            case Steps.Insurer:
                isValid = await validatePolicyholderStep();
                if (isValid) {
                    if (!formState.PolicyholderIsSame) {
                        setCurrentStep(Steps.Insured)
                    } else {
                        setCurrentStep(Steps.InsuranceTerm);
                    }
                };
                break;
            case Steps.Insured:
                isValid = await validateInsuredStep();
                if (isValid) setCurrentStep(Steps.InsuranceTerm);
                break;
            case Steps.InsuranceTerm:
                isValid = await validateInsuranceTermStep();
                if (isValid) setCurrentStep(Steps.FieldValidationStep);
                break;
            default:
                break;
        }
    };

    const handleBack = () => {
        switch (currentStep) {
            case Steps.Insured:
                setCurrentStep(Steps.Insurer);
                break;
            case Steps.InsuranceTerm:
                if (!formState.PolicyholderIsSame) {
                    setCurrentStep(Steps.Insured)
                } else {
                    setCurrentStep(Steps.Insurer);
                }
                break;
            case Steps.FieldValidationStep:
                setCurrentStep(Steps.InsuranceTerm);
                break;
            default:
                break;
        }
    };
    useEffect(() => {
        if (formState.BeginDate) {
            const [day, month, year] = formState.BeginDate.split('.').map(Number);
            const beginDate = new Date(year, month - 1, day);
            beginDate.setFullYear(beginDate.getFullYear() + 1);
            beginDate.setDate(beginDate.getDate() - 1);
            const formattedDate = [
                String(beginDate.getDate()).padStart(2, '0'),
                String(beginDate.getMonth() + 1).padStart(2, '0'),
                beginDate.getFullYear()
            ].join('.');
            dispatch(updateField({ field: 'EndDate', value: formattedDate }));
        }
    }, [formState.BeginDate]);

    const wrappedHandleBlur = (fieldName: NestedKeyOf<IStepTwo>) => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const inputEvent = e as React.FocusEvent<HTMLInputElement>;
        handleBlur(fieldName)(inputEvent);
    };

    const emailDomains = [
        'gmail.com',
        'yandex.ru',
        'mail.ru',
        'rambler.ru',
        'yahoo.com',
        'outlook.com',
        'icloud.com',
        'bk.ru',
        'inbox.ru',
        'list.ru',
    ];

    const [emailInput, setEmailInput] = useState(formState.Insurer.Email);

    useEffect(() => {
        setEmailInput(formState.Insurer.Email);
    }, [formState.Insurer.Email]);

    const handleEmailInputChange = (event: React.ChangeEvent<{}>, newValue: string) => {
        setEmailInput(newValue);
        handleAutocompleteChange('Insurer.Email')(newValue);
    };

    const handleDomainSelect = (event: React.ChangeEvent<{}>, newValue: string | null) => {
        if (newValue) {
            const [localPart] = emailInput.split('@');
            const newEmail = `${localPart}@${newValue}`;
            setEmailInput(newEmail);
            handleAutocompleteChange('Insurer.Email')(newEmail);
        }
    };

    const filterDomains = (options: string[], state: { inputValue: string }) => {
        const inputValue = state.inputValue.trim();
        if (!inputValue.includes('@')) {
            return [];
        }
        const domainPart = inputValue.split('@')[1] || '';
        return options.filter(option =>
            option.toLowerCase().includes(domainPart.toLowerCase())
        );
    };
    const insurerKeys: (keyof IInsurer)[] = [
        'LastName',
        'FirstName',
        'MiddleName',
        'BirthDate',
        'Gender',
        'Address',
        'CountryID',
        'Phone',
        'Email',
        'DocumentDate',
        'DocumentDepartment',
        'DepartmentCode',
        'DocumentNumber',
        'DocumentSeries'
    ];
    const isEqualInsurer = (a: IInsurer, b: IInsurer): boolean => {
        return insurerKeys.every(key => a[key] === b[key]);
    };
    useEffect(() => {
        if (formState.PolicyholderIsSame) {
            const updatedInsureds = formState.Insureds.map((insured, index) => {
                if (index === 0) {
                    return {
                        ...insured,
                        LastName: formState.Insurer.LastName,
                        FirstName: formState.Insurer.FirstName,
                        MiddleName: formState.Insurer.MiddleName,
                        BirthDate: formState.Insurer.BirthDate,
                        Gender: formState.Insurer.Gender,
                        Address: formState.Insurer.Address,
                        CountryID: formState.Insurer.CountryID,
                        Phone: formState.Insurer.Phone,
                        Email: formState.Insurer.Email,
                        DocumentDate: formState.Insurer.DocumentDate,
                        DocumentDepartment: formState.Insurer.DocumentDepartment,
                        DepartmentCode: formState.Insurer.DepartmentCode,
                        DocumentNumber: formState.Insurer.DocumentNumber,
                        DocumentSeries: formState.Insurer.DocumentSeries
                    };
                }
                return insured;
            });
            dispatch(updateField({ field: 'Insureds', value: updatedInsureds }));
        } else {
            if (formState.Insureds.length > 0 && isEqualInsurer(formState.Insureds[0], formState.Insurer)) {
                const updatedInsureds = formState.Insureds.map((insured, index) => {
                    if (index === 0) {
                        return {
                            ...insured,
                            LastName: '',
                            FirstName: '',
                            MiddleName: '',
                            BirthDate: '',
                            Gender: '',
                            Address: '',
                            CountryID: '',
                            Phone: '',
                            Email: '',
                            DocumentDate: '',
                            DocumentDepartment: '',
                            DepartmentCode: '',
                            DocumentNumber: '',
                            DocumentSeries: ''
                        };
                    }
                    return insured;
                });
                dispatch(updateField({ field: 'Insureds', value: updatedInsureds }));
            }
        }
    }, [formState.PolicyholderIsSame, formState.Insurer]);

    function getTomorrowDate(): string {
        const now = new Date();
        now.setDate(now.getDate() + 1);
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        return `${day}.${month}.${year}`;
    }
    useEffect(() => {
        if (!formState.BeginDate) {
            dispatch(updateField({ field: 'BeginDate', value: getTomorrowDate() }));
        }

    }, []);

    return (
        <>
            {currentStep === Steps.InsuranceTerm && (
                <InsuranceTermStep
                    formState={formState}
                    errors={errors}
                    handleNextStep={handleNextStep}
                    handleBack={handleBack}
                    openDatePicker={openDatePicker}
                    setOpenDatePicker={setOpenDatePicker}
                />
            )}
            {currentStep === Steps.Insurer && (
                <Insurer
                    formState={formState}
                    errors={errors}
                    handleNextStep={handleNextStep}
                    handleBack={handleBack}
                    openDatePicker={openDatePicker}
                    setOpenDatePicker={setOpenDatePicker}
                    handleChange={handleChange}
                    handleBlur={wrappedHandleBlur}
                    handleDateChange={handleDateChange}
                    formatPhone={formatPhone}
                    emailInput={emailInput}
                    handleEmailInputChange={handleEmailInputChange}
                    handleDomainSelect={handleDomainSelect}
                    filterDomains={filterDomains}
                    emailDomains={emailDomains}
                    handlePhoneChange={handlePhoneChange}
                    handleAutocompleteChange={handleAutocompleteChange}
                    handleCheckboxChange={handleCheckboxChange}
                    handleDepartmentCodeChange={handleDepartmentCodeChange}
                    handleDocumentNumberChange={handleDocumentNumberChange}
                    handleDocumentSeriesChange={handleDocumentSeriesChange}
                    handleAddressChange={handleAutocompleteChange}
                    addressSuggestions={DaDataAddres.Suggestions}
                    isLoadingAddress={DaDataAddres.isLoadingDaDataAddres}
                />
            )}
            {currentStep === Steps.Insured && (
                <Insured
                    formState={formState}
                    errors={errors}
                    handleBack={handleBack}
                    handleChange={handleChange}
                    handleBlur={wrappedHandleBlur}
                    handleCheckboxChange={handleCheckboxChange}
                    handleArrayDateChange={handleArrayDateChange}
                    handleNext={handleNextStep}
                    formatPhone={formatPhone}
                    handleArrayPhoneChange={handleArrayPhoneChange}
                />
            )}
            {currentStep === Steps.FieldValidationStep && (
                <FieldValidationStep
                    formState={formState}
                    errors={errors}
                    handleNextStep={handleNext}
                    handleBack={handleBack}
                    formatPhone={formatPhone}
                />
            )}
        </>

    );
};